import { type CSSProperties, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, PageLayout } from '@sentinel/ui';
import { takeResumedResult } from '@sentinel/api-client';
import { type StepUpGrantError, useStepUpGrant } from './use-step-up-grant';
import {
  classifyGrantError,
  grantExpiresIn,
  isGrantResumed,
  matchesGrantPost,
  rememberGranted,
  wasGranted,
  type GrantErrorKind,
} from './step-up-result';

type ShownError = Exclude<GrantErrorKind, 'cancelled'>;

const ERROR_KEYS: Record<ShownError, string> = {
  expired: 'step_up.error_expired',
  other_user: 'step_up.error_other_user',
  unavailable: 'step_up.error_unavailable',
  generic: 'step_up.error_generic',
};

const bodyStyle: CSSProperties = { display: 'grid', gap: '1rem', maxWidth: '40rem' };

export function McpStepUpPage({ ticket }: { ticket: string | undefined }) {
  const { t } = useTranslation('mcp');
  const grant = useStepUpGrant();
  // Always start with the confirm button: the stored flag only says a grant
  // for this ticket id once succeeded here, not that a grant exists now. A
  // ticket that survived (its delete failed) can be handed out again after
  // that grant expired, and must stay usable.
  const [done, setDone] = useState(false);
  const [error, setError] = useState<ShownError | null>(ticket ? null : 'expired');

  const markDone = (expiresInSecs: number) => {
    if (ticket) rememberGranted(ticket, expiresInSecs);
    setError(null);
    setDone(true);
  };

  const showError = (status: number | undefined, code: string | undefined, replayed = false) => {
    const kind = classifyGrantError(status, code);
    // The ticket is deleted when the grant succeeds, so a 404 while this
    // browser's own grant is still valid means "already done", not "expired".
    if (kind === 'expired' && ticket && wasGranted(ticket)) {
      setError(null);
      setDone(true);
      return;
    }
    // In the page, a step_up_required means the user closed the dialog. A
    // replay after a federated step-up has no dialog: the IdP's sign-in did
    // not meet the ticket, so say it did not go through.
    if (kind === 'cancelled') {
      if (replayed) setError('generic');
      return;
    }
    setError(kind);
  };

  // A federated (SSO) step-up is a full-page round trip. The root route replays
  // the grant POST after the IdP returns and publishes its JSON result; pick it
  // up on mount and on the resume event (same pattern as account.credentials).
  useEffect(() => {
    const consume = () => {
      const result = takeResumedResult();
      if (!result || !matchesGrantPost(result)) return;
      if (isGrantResumed(result)) {
        markDone(grantExpiresIn(result.body));
      } else if (!result.ok) {
        // For example the ticket expired while the user was at the IdP.
        showError(result.status, (result.body as { error?: string } | null)?.error, true);
      }
    };
    consume();
    window.addEventListener('sentinel:step-up-resumed', consume);
    return () => window.removeEventListener('sentinel:step-up-resumed', consume);
  }, []);

  async function handleConfirm() {
    if (!ticket) return;
    setError(null);
    try {
      const res = await grant.mutateAsync({ ticket });
      markDone(grantExpiresIn(res));
    } catch (e) {
      const err = e as StepUpGrantError;
      showError(err.status, err.body?.error);
    }
  }

  return (
    <PageLayout title={t('step_up.title')} subtitle={t('step_up.description')}>
      <div style={bodyStyle}>
        {done ? (
          <Alert type="success" role="status" data-testid="mcp-step-up-done">
            {t('step_up.done')}
          </Alert>
        ) : null}
        {!done && error ? (
          <Alert type="error" role="alert" data-testid="mcp-step-up-error">
            {t(ERROR_KEYS[error])}
          </Alert>
        ) : null}
        {!done && ticket && error !== 'expired' && error !== 'other_user' && error !== 'unavailable' ? (
          <div>
            <Button
              type="button"
              data-testid="mcp-step-up-confirm"
              onClick={() => void handleConfirm()}
              disabled={grant.isPending}
            >
              {t('step_up.confirm')}
            </Button>
          </div>
        ) : null}
      </div>
    </PageLayout>
  );
}
